import { FunctionComponent, useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

import AppLogo from "@/assets/images/favicon.png";

import { ROUTE_CONFIG } from "@/routes/config";

import { Button } from "@/components/Button/Button";
import { ButtonColor, ButtonSize } from "@/components/Button/types";

import { useFinalizeCheckoutSessionMutation } from "@/pages/Private/redux/subscription/subscription.api";
import { useUpdateMeMutation } from "@/pages/Private/redux/account/account.api";

export const SelectSubscriptionDone: FunctionComponent = () => {
	const navigate = useNavigate();

	const { t } = useTranslation();
	const ts = (key: string) => t(`usage.subscription.checkout.done.${key}`);

	const [searchParams, setSearchParams] = useSearchParams();

	const [finalizeCheckoutSession, { isLoading: isFinalizingCheckoutSession }] =
		useFinalizeCheckoutSessionMutation();

	const [finalizeFailed, setFinalizeFailed] = useState(false);

	// The session id is stripped from the URL as soon as it is read, so it is kept here for the
	// retry below — a reload would otherwise be the only way to try again, and by then it is gone.
	const sessionIdRef = useRef<string | null>(null);

	const runFinalize = useCallback(
		(sessionId: string) => {
			setFinalizeFailed(false);

			finalizeCheckoutSession({ sessionId })
				.unwrap()
				.catch((err: unknown) => {
					// PREC-3917: this used to be an unhandled rejection behind an unconditional
					// "You're all set." — the one screen that could have shown the customer their
					// subscription had not been recorded instead congratulated them on it. The
					// webhook now records it server-side regardless, so this is the second line of
					// defence, but a silent failure here must never look like success again.
					console.error(err);
					setFinalizeFailed(true);
				});
		},
		[finalizeCheckoutSession]
	);

	useEffect(() => {
		const sessionId = searchParams.get("session_id");

		if (sessionId) {
			sessionIdRef.current = sessionId;
			runFinalize(sessionId);
			setSearchParams({}, { replace: true });
		}
	}, [searchParams, runFinalize, setSearchParams]);

	const [updateMe, { isLoading: isUpdatingMe }] = useUpdateMeMutation();

	const handleUpdateMe = async () => {
		await updateMe({
			isOnboarded: true,
		}).unwrap();

		navigate(ROUTE_CONFIG.LISTS);
	};

	const handleRetry = () => {
		if (sessionIdRef.current) {
			runFinalize(sessionIdRef.current);
		}
	};

	const renderHeader = (
		<>
			<img alt="logo" className="w-[48px] h-[48px] mb-4" src={AppLogo} />
			<div className="text-center text-md font-bold mb-4 mx-[94px]">{ts("title")}</div>
			<div className="text-center text-ssm font-normal mb-8 text-text-meta mx-[94px]">
				<span className="text-transparent bg-gradient-to-r from-[#1D3DF2] to-[#B771EE] bg-clip-text font-bold">
					{ts("headline")}
				</span>
				<br />
				{ts("body")}
			</div>
		</>
	);

	const renderFailedHeader = (
		<>
			<img alt="logo" className="w-[48px] h-[48px] mb-4" src={AppLogo} />
			<div className="text-center text-md font-bold mb-4 mx-[94px]">{ts("failedTitle")}</div>
			<div className="text-center text-ssm font-normal mb-8 text-text-meta mx-[94px]">
				{ts("failedBody")}
			</div>
		</>
	);

	return (
		<div className={`w-full flex flex-col items-center justify-center `}>
			<div className="flex flex-col bg-white px-6 py-10 rounded-[20px] w-[656px] max-w-[100%] shadow-[0px_0px_80px_0px_rgba(171,178,217,0.35)] items-center justify-center mb-6">
				{finalizeFailed ? renderFailedHeader : renderHeader}

				<div className="w-auto flex gap-3">
					{finalizeFailed && (
						<Button
							color={ButtonColor.ACTION_SECONDARY}
							isLoading={isFinalizingCheckoutSession}
							size={ButtonSize.ML}
							title={ts("retry")}
							onClick={handleRetry}
						/>
					)}

					<Button
						color={ButtonColor.ACTION}
						isLoading={isFinalizingCheckoutSession || isUpdatingMe}
						size={ButtonSize.ML}
						title={ts("cta")}
						onClick={handleUpdateMe}
					/>
				</div>
			</div>
		</div>
	);
};
